import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";
import { getRevAndTraining, uploadJsonToS3, adjustSiblingOrderAndInsert, prepareCurrentSnapshot } from "../util/AppUtils";
import { v4 as uuidv4 } from 'uuid';
import { updateCurrentSnapshot } from "./procedureRevisionSlice";

const baseUrl = BASE_URL + "video-tree-node";

export const createVideo = createAsyncThunk(
  "video/create",
  async (
    {
      jsonData,
      comment,
      version_content,
      allNodesData,
      partnumber,
      revision,
    },
    { dispatch, rejectWithValue }
  ) => {
    const tree_node_id = uuidv4();

    const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));

    const ad = await getAuditTrailData({
      action: "CREATE_VIDEO",
      comment: comment,
      version_content: {
        ...version_content,
        tree_node_id,
      },
    });

    const newVideoNode = {
      tree_node_id: tree_node_id,
      type: "video",
      sibling_order: jsonData.sibling_order,
      section_id_ref: jsonData.section_id_ref,
      step_id_ref: jsonData.step_id_ref,
      substep_id_ref: jsonData.substep_id_ref || null,
      created_at: new Date().toISOString(),
      is_deleted: false,
      detail: {
        ...jsonData,
        video_tree_node_id: `vid_ref_${Date.now()}`,
        videos: [
          {
            video: jsonData.video,
            video_id: `vid_${Date.now()}`,
            video_tree_node_ref: `vid_ref_${Date.now()}`,
          },
        ],
      },
      isDraft: true,
    };

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    snapshotPayload.forEach((section) => {
      if (
        section.section_id_ref === jsonData.section_id_ref ||
        section.tree_node_id === jsonData.section_id_ref
      ) {
        if (section.steps) {
          section.steps.forEach((step) => {
            if (
              step.step_id_ref === jsonData.step_id_ref ||
              step.tree_node_id === jsonData.step_id_ref
            ) {
              let targetChildren;
              if (jsonData.substep_id_ref) {
                if (step.substeps) {
                  const substep = step.substeps.find(
                    (ss) =>
                      ss.substep_id_ref === jsonData.substep_id_ref ||
                      ss.tree_node_id === jsonData.substep_id_ref
                  );
                  if (substep) {
                    targetChildren = substep.children;
                    if (!targetChildren) {
                      substep.children = [];
                      targetChildren = substep.children;
                    }
                    adjustSiblingOrderAndInsert(targetChildren, newVideoNode);
                  }
                }
              } else {
                targetChildren = step.children;
                if (!targetChildren) {
                  step.children = [];
                  targetChildren = step.children;
                }
                adjustSiblingOrderAndInsert(targetChildren, newVideoNode);
              }
            }
          });
        }
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(
      allNodesData,
      partnumber,
      revision + "_video_previous",
      true
    );

    const revAndTrainingData = await getRevAndTraining(dispatch, jsonData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3(
      { data: snapshotPayload, ...ad.version_content, operation: "single_audit_trail", ...revAndTrainingData },
      partnumber,
      revision + "_video_new",
      true
    );

    // Add audit_trail_data to JSON payload
    const payload = {
      ...jsonData,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          ...ad.version_content,
          action_to_show: ad.version_content.action_to_show,
        },
      },
    };

    try {
      const response = await axios.post(`${baseUrl}/create`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      await dispatch(
        updateCurrentSnapshot({
          partId: jsonData.part_required_id_ref,
          revisionId: jsonData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      console.error("Video creation error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Error creating video");
    }
  }
);

// 📌 Update Video (PUT)
export const updateVideo = createAsyncThunk(
  "video/update",
  async (
    {
      id,
      jsonData,
      comment,
      version_content,
      allNodesData,
      partnumber,
      revision,
      tree_node_id,
      parent_version,
    },
    { dispatch, rejectWithValue }
  ) => {
    const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));

    const trimmedParentVersion =
      parent_version != null && String(parent_version).trim() !== ""
        ? String(parent_version).trim()
        : null;

    const ad = await getAuditTrailData({
      action: "UPDATE_VIDEO",
      comment: comment,
      ...(trimmedParentVersion ? { parent_version: trimmedParentVersion } : {}),
      version_content,
    });

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    // Update the node in snapshotPayload
    snapshotPayload.forEach((section) => {
      if (
        section.section_id_ref === jsonData.section_id_ref ||
        section.tree_node_id === jsonData.section_id_ref
      ) {
        if (section.steps) {
          section.steps.forEach((step) => {
            if (
              step.step_id_ref === jsonData.step_id_ref ||
              step.tree_node_id === jsonData.step_id_ref
            ) {
              let targetChildren;
              if (jsonData.substep_id_ref) {
                if (step.substeps) {
                  const substep = step.substeps.find(
                    (ss) =>
                      ss.substep_id_ref === jsonData.substep_id_ref ||
                      ss.tree_node_id === jsonData.substep_id_ref
                  );
                  if (substep) {
                    targetChildren = substep.children;
                  }
                }
              } else {
                targetChildren = step.children;
              }

              if (targetChildren) {
                const nodeIndex = targetChildren.findIndex(
                  (child) =>
                    child.tree_node_id === id ||
                    child.detail?.video_tree_node_id === id
                );

                if (nodeIndex !== -1) {
                  const existingNode = targetChildren[nodeIndex];
                  existingNode.detail = {
                    ...existingNode.detail,
                    ...jsonData,
                    videos: jsonData.video
                      ? [
                          {
                            video: jsonData.video,
                            video_id:
                              existingNode.detail?.videos?.[0]?.video_id ||
                              `vid_${Date.now()}`,
                          },
                        ]
                      : existingNode.detail?.videos,
                  };
                  existingNode.sibling_order = jsonData.sibling_order;
                }
              }
            }
          });
        }
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(
      allNodesData,
      partnumber,
      revision + "_video_previous",
      true
    );

    const revAndTrainingData = await getRevAndTraining(dispatch, jsonData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3(
      { data: snapshotPayload, ...version_content, tree_node_id, operation: "single_audit_trail", ...revAndTrainingData },
      partnumber,
      revision + "_video_new",
      true
    );

    // Add audit_trail_data to JSON payload
    const payload = {
      ...jsonData,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          ...version_content,
          action_to_show: version_content.action_to_show,
        },
      },
    };

    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 300000, // 5 minutes timeout for large video uploads
      });
      
      await dispatch(
        updateCurrentSnapshot({
          partId: jsonData.part_required_id_ref,
          revisionId: jsonData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating video");
    }
  }
);

// 📌 Get All Videos (GET)
export const getAllVideos = createAsyncThunk(
  "video/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching videos");
    }
  }
);

// 📌 Delete Video (DELETE)
export const deleteVideo = createAsyncThunk(
  "video/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting video");
    }
  }
);

// 🛠 Video Slice
const videoSlice = createSlice({
  name: "video",
  initialState: {
    videos: [],
    loading: false,
    error: null,
    // Temporary video data for unsaved videos (persists across navigation)
    tempVideoData: {
      videoFile: null, // Will store { name, type, size, lastModified, base64Data }
      videoUrl: "",
      shortDescription: "",
      preview: "",
      hasUnsavedChanges: false,
      sessionId: null, // To identify which session this data belongs to
    },
  },
  reducers: {
    // Store temporary video data (file, URL, description, etc.)
    setTempVideoData: (state, action) => {

      state.tempVideoData = { ...state.tempVideoData, ...action.payload };

    },
    // Clear temporary video data (after successful save or when explicitly cleared)
    clearTempVideoData: (state) => {

      state.tempVideoData = {
        videoFile: null,
        videoUrl: "",
        shortDescription: "",
        preview: "",
        hasUnsavedChanges: false,
        sessionId: null,
      };

    },
    // Update only the hasUnsavedChanges flag
    setHasUnsavedChanges: (state, action) => {
      state.tempVideoData.hasUnsavedChanges = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createVideo.pending, (state) => {
        state.loading = true;
      })
      .addCase(createVideo.fulfilled, (state, action) => {
        state.loading = false;
        state.videos.push(action.payload);
      })
      .addCase(createVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(updateVideo.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateVideo.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = state.videos.map((video) =>
          video.id === action.payload.id ? action.payload : video
        );
      })
      .addCase(updateVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getAllVideos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = action.payload.results;
      })
      .addCase(getAllVideos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(deleteVideo.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = state.videos.filter(
          (video) => video.id !== action.payload
        );
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      });
  },
});

// Export actions
export const { setTempVideoData, clearTempVideoData, setHasUnsavedChanges } =
  videoSlice.actions;

export default videoSlice.reducer;
